import { FC, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Popover } from 'antd';

import { SvgIcon } from '@/components/SvgIcon';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { notificationsSelectors } from '@/redux/selectors';
import { notificationsActions } from '@/redux/actions';
import { navigationPages } from '@/config';
import { navigateToNotificationLink } from '@/api/amlbotAPI/notifications';
import { NotificationItem } from '@/redux/models';

import { NotificationList } from './NotificationList';
import { NotificationModal } from './NotificationModal';
import styles from './NotificationBell.module.sass';

export const NotificationBell: FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const unreadCount = useAppSelector(notificationsSelectors.getUnreadCount);
  const [open, setOpen] = useState(false);
  // The notification shown in the popup. Kept here, outside the popover, so closing
  // the panel does not unmount the popup with it.
  const [openedItem, setOpenedItem] = useState<NotificationItem | null>(null);

  useEffect(() => {
    dispatch(notificationsActions.fetchUnreadCount());
  }, [dispatch]);

  const openChangeHandler = (next: boolean) => {
    setOpen(next);

    // Refetch on open so the panel reflects anything that arrived while closed, and
    // so an optimistically-marked-read item gets re-synced after a failed markRead
    // (the saga repairs the badge but deliberately leaves `items` alone).
    //
    // Skip it on the All Notifications page: that page renders the SAME Redux list
    // and may have paged deep, and this fetch is cursor-less, so it lands in
    // `setData` and REPLACES items — collapsing an 80-row page back to 20 rows
    // underneath the open popover. The page fetches on its own mount, so skipping
    // here loses nothing.
    if (next && location.pathname !== navigationPages.NOTIFICATIONS.path) {
      dispatch(notificationsActions.fetchNotifications());
    }
  };

  // The panel closes when — and only when — the route actually changed. A
  // notification without a link, which is most of them, marks itself read in place
  // and leaves the panel open, so the customer sees the unread highlight clear and
  // can keep reading the rest. Closing on every click made reading one item look
  // like the panel had dismissed itself and nothing had happened.
  //
  // Driven off the completed route change rather than from inside the click handler,
  // so it covers every way the panel can take you somewhere — an item's own link, a
  // link inside a campaign message, the View all footer — with one rule instead of
  // three call sites that each have to remember. Nothing is needed for external
  // links: those leave the SPA entirely.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const navigateHandler = (link: string | null | undefined) => {
    navigateToNotificationLink(link, navigate);
  };

  const openItemHandler = (item: NotificationItem) => {
    setOpen(false);
    setOpenedItem(item);
  };

  return (
    <>
      <Popover
        open={open}
        onOpenChange={openChangeHandler}
        trigger="click"
        placement="bottomRight"
        arrow={false}
        zIndex={9999}
        content={
          <NotificationList
            onNavigate={navigateHandler}
            onClose={() => setOpen(false)}
            onOpen={openItemHandler}
          />
        }
      >
        <button
          type="button"
          className={styles.bell}
          aria-label={t('translation.notifications.title')}
        >
          <SvgIcon name="notification" />
          {unreadCount > 0 && (
            <span className={styles.badge}>
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>
      </Popover>
      <NotificationModal
        item={openedItem}
        onNavigate={navigateHandler}
        onClose={() => setOpenedItem(null)}
      />
    </>
  );
};
